<template>
  <div class="screen-view-container">
    <div
      v-if="screenConfig"
      v-loading="loading"
      class="screen-view"
      :style="{
        width: screenConfig.panelWidth + 'px',
        height: screenConfig.panelHeight + 'px',
        background: screenConfig.backgroundType === 1 ? screenConfig.background:`url(${screenConfig.background}) no-repeat 100% 100%/100% 100%`,
      }">
      <template v-if="!isJumping">
        <div
          v-for="(item,index) in screenConfig.chartViews"
          v-show="showComponentInView(item)"
          :key="screenId + '-' + screenConfig.panelSn + '-' + item.addSn"
          :style="{
            width: item.chartWidth + 'px',
            height: item.chartHeight + 'px',
            zIndex: item.sort,
            transform: `translate(${item.chartX+'px'},${item.chartY+'px'})`
          }"
          class="screen-component-item">
          <!-- 各个组件 -->
          <component
            :is="item.chartComponentType"
            :ref="`chartComponent${index}`"
            :config="item"
            :key="screenId + '-' + screenConfig.panelSn + '-' + item.addSn + '-' + index"
            :component-type="item.chartComponent"
            :global-config="screenConfig"
            :filter-params="filterParams"
            :is-disabled="item.chartComponentType==='FilterBlock'"
            component-status="view"
            @click.native="handleJump(item)"
            @filterChange="updateComponentData" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import TextBlock from '@/components/charts/text.vue'
import TimeClock from '@/components/charts/timeClock.vue'
import MapChart from '@/components/charts/mapChart.vue'
import PieChart from '@/components/charts/pieChart.vue'
import LineChart from '@/components/charts/lineChart.vue'
import BarChart from '@/components/charts/barChart.vue'
import Number from '@/components/charts/number/number.vue'
import IndependentNumber from '@/components/charts/number/independentNumber.vue'
import Gauge from '@/components/charts/number/gauge.vue'
import ScrollTable from '@/components/charts/table/scrollTable.vue'
import Rank from '@/components/charts/rank.vue'
import RichTextBlock from '@/components/charts/material/showRich.vue'
import BorderBlock from '@/components/charts/material/borderBlock.vue'
import FilterBlock from '@/components/charts/material/filterBlock.vue'
import HeatMapChart from '@/components/charts/heatMap.vue'
import SignName from '@/components/charts/material/signName.vue'
import ImageBlock from '@/components/charts/image.vue'
import _ from 'lodash'
import { getScreenDataApi } from './fetch'
import { needIndexComponent } from './funcs'
import { defaultFilterDate } from './data'
import { orderFilterDateRange } from '@/utils/filterDateRange'
import { coerceSelectedIndicatorListOnChartViewData } from '@/utils/indicatorSelect'
export default {
  name: 'ScreenView',
  components: {
    TextBlock, // 文本
    TimeClock, // 当前时间
    MapChart, // 地图
    PieChart, // 饼图
    LineChart, // 折线图
    BarChart, // 垂直柱状图 / 水平柱状图
    Number, // 纯数字
    IndependentNumber, // 独立数字
    Gauge, // 仪表板
    ScrollTable, // 滚动列表
    Rank, // 排行榜
    RichTextBlock, // 富文本编辑
    BorderBlock, // 边框
    FilterBlock, // 筛选框
    HeatMapChart, // 热力图（实际是散点图）
    SignName, // 签名
    ImageBlock,

  },
  data () {
    return {
      screenConfig: null,
      isJumping: false,
      jumpPanelSn: null,
      loading: false,
      jumpDelaySecond: 0,
      timer: null,
      filterParams: {
        startTime: defaultFilterDate.startTime,
        endTime: defaultFilterDate.endTime,
      },
      intervalTimer: null,
      /** 防止快速切换路由时，较慢的旧 init 请求完成后覆盖当前大屏数据 */
      viewInitSerial: 0,
    }
  },
  computed: {
    screenId () {
      return this.$route.params.id
    },
  },
  watch: {
    async screenId (newVal) {
      if (newVal) {
        await this.$nextTick()
        this.init(true)
      }
    },
  },
  created () {
    /** 防抖窗口内合并多次 filterChange（日期 + 地区），避免只保留最后一次 emit 丢字段 */
    this._coalescedUpdatePayload = null
    this._debouncedUpdateComponentData = _.debounce(() => {
      const fp = this._coalescedUpdatePayload
      this._coalescedUpdatePayload = null
      if (fp && typeof fp === 'object' && Object.keys(fp).length) {
        this._runUpdateComponentData(fp)
      }
    }, 60)
    this.init()
  },
  beforeDestroy () {
    const d = this._debouncedUpdateComponentData
    if (d && typeof d.cancel === 'function') d.cancel()
    this._coalescedUpdatePayload = null
    clearTimeout(this.timer)
    this.timer = null
    clearInterval(this.intervalTimer)
    this.intervalTimer = null
  },
  mounted () {
    const that = this
    window.addEventListener('popstate', () => {
      clearTimeout(that.timer)
      that.timer = null
    })
    window.addEventListener('pushstate', () => {
      clearTimeout(that.timer)
      that.timer = null
    })
    // 15分钟 定时刷新数据（不走 filterChange 合并队列，直接按当前 filterParams 拉数）
    this.intervalTimer = setInterval(() => {
      this._runUpdateComponentData(undefined)
    }, 15 * 60 * 1000)
  },
  methods: {
    /** 筛选组件可配置「查看大屏时隐藏」：不占视觉但仍挂载以参与筛选 */
    showComponentInView (item) {
      if (item.chartComponent !== 'filterBlock') return true
      return !item.chartSet?.chartStyle?.hideInView
    },
    coerceChartViewDataSelectedIndicatorList (chartViewData) {
      coerceSelectedIndicatorListOnChartViewData(chartViewData)
    },
    async init (jump = false) {
      const gen = ++this.viewInitSerial
      if (jump) {
        this.isJumping = true
      }
      try {
        // 切换大屏后沿用上一屏的省市区筛选，会在 updateComponentData 里把地图 map 写成旧区划；先清掉区划再拉数
        const fp = this.filterParams || {}
        const st0 = fp.startTime || defaultFilterDate.startTime
        const et0 = fp.endTime || defaultFilterDate.endTime
        const ord = orderFilterDateRange(st0, et0)
        this.filterParams = {
          startTime: ord.startTime || defaultFilterDate.startTime,
          endTime: ord.endTime || defaultFilterDate.endTime,
          panelSn: this.screenId,
        }
        // this.loading = true
        // 获取大屏数据
        const res = await getScreenDataApi(this.screenId)
        if (gen !== this.viewInitSerial) return
        res.chartViews.forEach(item => {
          item.chartSet = JSON.parse(item.chartSet)
          if (Object.prototype.hasOwnProperty.call(item, 'chartColors')) {
            const legacy = item.chartColors
            if (legacy != null && legacy !== '' && legacy !== 'inherit' &&
                (item.chartSet.chartColors == null || item.chartSet.chartColors === '')) {
              this.$set(item.chartSet, 'chartColors', legacy)
            }
            this.$delete(item, 'chartColors')
          }
          // 如果数据来源是指标库，则请求指标数据
          if ((needIndexComponent(item.chartComponent)) && item.chartSet?.dataSource === 'indicatorLib') {
            const parseData = JSON.parse(item.chartViewData.chartData)
            const chartData = Object.prototype.toString.call(parseData) === '[object Object]' ? parseData : {}
            const initData = JSON.parse(item.chartViewData.initData)
            item.chartViewData = {
              ...item.chartViewData,
              ...chartData,
              initData,
            }
          } else {
            const chartData = JSON.parse(item.chartViewData.chartData)
            const initData = JSON.parse(item.chartViewData.initData)
            item.chartViewData = {
              ...item.chartViewData,
              chartData,
              initData,
            }
          }
          this.coerceChartViewDataSelectedIndicatorList(item.chartViewData)
          // 兼容独立数字组件和边框组件老数据
          if (item.chartComponentType === 'IndependentNumber' && !item.chartSet.borderStyle?.numberBorderType) {
            item.chartSet.borderStyle.numberBorderType = 1
          }
          if (item.chartComponentType === 'BorderBlock' && !item.chartSet.chartStyle?.borderType) {
            item.chartSet.chartStyle.borderType = 1
          }
        })
        if (res.backgroundType === 1) {
          res.background = res.backgroundColor
        } else {
          res.background = res.backgroundTemplate
        }
        this.jumpPanelSn = res?.jumpPanelSn || null
        this.jumpDelaySecond = res.jumpDelaySecond ? res.jumpDelaySecond * 1000 : null
        this.screenConfig = res
        // 接口返回的 filterParams 含日期+省市区；须写回父级，否则子组件虽能读 globalConfig，:filter-params 与拉数仍缺区划
        if (res.filterParams) {
          try {
            const parsed = JSON.parse(res.filterParams)
            const { startTime, endTime, ...rest } = {
              ...this.filterParams,
              ...parsed,
              panelSn: this.screenId,
            }
            const o = orderFilterDateRange(
              startTime || defaultFilterDate.startTime,
              endTime || defaultFilterDate.endTime,
            )
            this.filterParams = {
              ...rest,
              panelSn: this.screenId,
              startTime: o.startTime || defaultFilterDate.startTime,
              endTime: o.endTime || defaultFilterDate.endTime,
            }
          } catch (e) {
            this.filterParams = { ...this.filterParams, panelSn: this.screenId }
          }
        } else {
          this.filterParams = { ...this.filterParams, panelSn: this.screenId }
        }
        if (this.jumpDelaySecond && this.jumpPanelSn) {
          this.timer = setTimeout(() => {
            this.$router.push({ path: `/screen/view/${this.jumpPanelSn}` })
          }, this.jumpDelaySecond)
        }
        this.isJumping = false
        await this.$nextTick()
        if (gen !== this.viewInitSerial) return
        this.updateComponentData(this.filterParams)
      } catch (e) {
        this.$error(e)
      } finally {
        this.isJumping = false
      }
    },

    // 获取预览数据
    getPreviewData () {
      if (this.$route.path === '/screen/preview') {
        const LOCAL_PREVIEW_FULL_SCREEN = localStorage.getItem('LOCAL_PREVIEW_FULL_SCREEN')

        if (LOCAL_PREVIEW_FULL_SCREEN) {
          this.screenConfig = JSON.parse(LOCAL_PREVIEW_FULL_SCREEN)
        }
      }
    },

    handleJump (item) {
      if (item.jumpPanelSn) {
        this.$router.push({ path: `/screen/view/${item.jumpPanelSn}` })
      }
    },
    // 调用各个组件的 获取数据方法（有 payload 时防抖合并；无参时为定时刷新）
    updateComponentData (filterParams) {
      if (filterParams != null && typeof filterParams === 'object') {
        this._coalescedUpdatePayload = {
          ...(this._coalescedUpdatePayload ?? {}),
          ...filterParams,
        }
        this._debouncedUpdateComponentData()
        return
      }
      this._runUpdateComponentData(undefined)
    },
    _runUpdateComponentData (filterParams) {
      this.filterParams.panelSn = this.$route.params.id
      const keys = filterParams ? Object.keys(filterParams) : []
      // 仅当「本次传入」含省市区字段时才用筛选同步地图，避免 init/定时刷新沿用合并对象里残留的旧区划
      const incomingTouchesRegion =
        filterParams &&
        typeof filterParams === 'object' &&
        (Object.prototype.hasOwnProperty.call(filterParams, 'provinceCode') ||
          Object.prototype.hasOwnProperty.call(filterParams, 'cityCode') ||
          Object.prototype.hasOwnProperty.call(filterParams, 'regionCode'))
      if (filterParams) {
        const { startTime, endTime, ...rest } = { ...(this.filterParams ?? {}), ...filterParams }
        const st = startTime || defaultFilterDate.startTime
        const et = endTime || defaultFilterDate.endTime
        const o = orderFilterDateRange(st, et)
        this.filterParams = {
          ...rest,
          startTime: o.startTime || defaultFilterDate.startTime,
          endTime: o.endTime || defaultFilterDate.endTime,
        }
      }
      // 调用各个组件更新数据方法
      Object.keys(this.$refs).forEach(key => {
        const component = Array.isArray(this.$refs[key]) ? this.$refs[key][0] : {}
        // 如果组件是地图组件，需要更新地图数据
        if (
          incomingTouchesRegion &&
          component?.config?.chartComponent === 'mapChart'
        ) {
          const { provinceCode, cityCode, regionCode } = this.filterParams
          if (provinceCode || cityCode || regionCode) {
            const mapData = regionCode ? ['100000', provinceCode, cityCode, regionCode] : (cityCode ? ['100000', provinceCode, cityCode] : ['100000', provinceCode])
            component.config.chartSet.chartStyle.map = mapData
          }
        }
        // 更新数据（筛选框勿再对自己拉数，避免与 filterChange 循环）
        if (
          component?.getChartData &&
          typeof component.getChartData === 'function' &&
          component?.config?.chartComponent !== 'filterBlock'
        ) {
          component.getChartData(this.filterParams)
        }
        if (component && component.config && component.config.chartComponent === 'filterBlock' && filterParams && filterParams.addSn && !keys.includes('startTime')) {
          if (component.config.addSn !== filterParams.addSn) {
            component.clearDate()
          }
        }
      })
    },
  },

}
</script>

<style lang="scss" scoped>
.screen-view-container{
  position: relative;
  overflow: auto;
  .screen-view{
    .screen-component-item{
      position: absolute;
      box-sizing: border-box;

    }
  }
}
</style>
